import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  CalendarCheck, Clock, CheckCircle2, TrendingUp, Wallet, UserCircle,
  CalendarRange, Compass, ChevronRight, Loader2, MapPin, Hotel, Ticket,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { bookingsApi, eventsApi, walletApi, resolveMediaUrl, type BookingListResponse } from "@/lib/api";
import { formatNaira } from "@/data/venues";

export const Route = createFileRoute("/dashboard/")({ component: DashboardOverview });

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  confirmed: { label: "Confirmed", cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  pending: { label: "Pending", cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
  manager_approval_pending: { label: "Awaiting approval", cls: "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400" },
  checked_in: { label: "Checked in", cls: "bg-blue-500/10 text-blue-600 dark:text-blue-400" },
  checked_out: { label: "Completed", cls: "bg-slate-500/10 text-slate-600 dark:text-slate-400" },
  cancelled: { label: "Cancelled", cls: "bg-rose-500/10 text-rose-600 dark:text-rose-400" },
  expired: { label: "Expired", cls: "bg-rose-500/10 text-rose-600 dark:text-rose-400" },
};

type Row = BookingListResponse & { isEvent?: boolean };

function DashboardOverview() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState<BookingListResponse[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      bookingsApi.myBookings(user.id).then((r) => r.data).catch(() => []),
      eventsApi.getMyRegistrations().catch(() => []),
    ])
      .then(([b, e]) => {
        setBookings(Array.isArray(b) ? b : []);
        setEvents(Array.isArray(e) ? e : []);
      })
      .finally(() => setLoading(false));
    walletApi.getWallet().then((w) => setBalance(w.availableBalance ?? w.balance ?? 0)).catch(() => setBalance(null));
  }, [user?.id]);

  const all = useMemo<Row[]>(() => {
    const venueRows: Row[] = bookings.map((b) => ({ ...b, isEvent: false }));
    const eventRows: Row[] = events.map((eb) => {
      const created = new Date(eb.createdAt || eb.eventDate).getTime();
      const expired = eb.paymentStatus !== "paid" && Date.now() - created > 30 * 60 * 1000;
      return {
        id: eb.id,
        venueName: eb.eventName,
        venueLocation: eb.eventLocation,
        venueImage: undefined,
        startDate: eb.eventDate,
        endDate: eb.eventDate,
        daysCount: 1,
        totalAmount: eb.totalAmount,
        paymentStatus: expired ? "expired" : eb.paymentStatus,
        status: expired ? "expired" : eb.paymentStatus === "paid" ? "confirmed" : "pending",
        bookingRef: eb.ticketCode || "TICKET",
        createdAt: eb.createdAt || eb.eventDate,
        isEvent: true,
      } as Row;
    });
    return [...venueRows, ...eventRows].sort(
      (a, b) => new Date(b.createdAt || b.startDate).getTime() - new Date(a.createdAt || a.startDate).getTime(),
    );
  }, [bookings, events]);

  const pending = all.filter((b) => b.paymentStatus !== "paid" && b.status !== "cancelled" && b.status !== "expired");
  const upcoming = all.filter((b) => b.paymentStatus === "paid" && !["checked_out", "cancelled", "expired"].includes(b.status));
  const totalSpent = all.filter((b) => b.paymentStatus === "paid").reduce((s, b) => s + b.totalAmount, 0);

  const stats = [
    { icon: CalendarCheck, label: "Total Bookings", value: String(all.length), tone: "amber" },
    { icon: Clock, label: "Pending", value: String(pending.length), tone: "blue" },
    { icon: CheckCircle2, label: "Upcoming", value: String(upcoming.length), tone: "emerald" },
    { icon: TrendingUp, label: "Total Spent", value: formatNaira(totalSpent), tone: "primary" },
  ] as const;

  const toneCls: Record<string, string> = {
    amber: "bg-amber-500/10 text-amber-600 dark:bg-amber-950/20 dark:text-amber-400",
    blue: "bg-blue-500/10 text-blue-600 dark:bg-blue-950/20 dark:text-blue-400",
    emerald: "bg-emerald-500/10 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-400",
    primary: "bg-primary/10 text-primary dark:bg-primary/20",
  };

  const quickActions = [
    { to: "/dashboard/bookings", label: "My Bookings", desc: "Reservations & tickets", icon: CalendarRange },
    { to: "/dashboard/wallet", label: "Wallet", desc: "Balance & withdrawals", icon: Wallet },
    { to: "/dashboard/profile", label: "Profile", desc: "Details & favourites", icon: UserCircle },
    { to: "/hotels", label: "Browse Venues", desc: "Find your next stay", icon: Compass },
  ] as const;

  const recent = all.slice(0, 4);

  return (
    <div className="space-y-5">
      {/* Welcome banner */}
      <div className="relative overflow-hidden rounded-3xl bg-primary px-6 py-6 text-white shadow-elegant sm:px-9 sm:py-8">
        <div className="pointer-events-none absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-white/[0.08] to-transparent" />
        <div className="pointer-events-none absolute -right-10 -top-10 size-40 rounded-full bg-white/[0.04]" />
        <div className="pointer-events-none absolute -bottom-8 -left-8 size-32 rounded-full bg-white/[0.04]" />
        <div className="relative z-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white/90 backdrop-blur-md">
              <span className="size-1.5 animate-pulse rounded-full bg-white" />
              Guest Dashboard
            </span>
            <h1 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl">
              Welcome back, {user?.name?.split(" ")[0] || "there"}
            </h1>
            <p className="mt-1.5 max-w-sm text-xs text-white/70">
              Your control room — manage bookings, wallet and profile all in one place.
            </p>
          </div>
          <Link
            to="/hotels"
            className="flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-xs font-bold text-primary shadow-lg transition-all hover:scale-[1.02] hover:bg-white/90 active:scale-95"
          >
            Browse Venues <ChevronRight className="size-3.5" />
          </Link>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stats.map((s) => (
          <div
            key={s.label}
            className="rounded-2xl border border-border/60 bg-card p-4 shadow-soft transition-all duration-300 hover:border-primary/10 hover:shadow-elegant"
          >
            <div className={`mb-3 grid size-10 place-items-center rounded-xl ${toneCls[s.tone]}`}>
              <s.icon className="size-5" />
            </div>
            <p className="font-display text-xl font-bold text-foreground">{loading ? "—" : s.value}</p>
            <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Wallet + Quick actions */}
      <div className="grid gap-4 lg:grid-cols-[1.15fr_1.6fr]">
        {/* Wallet snapshot */}
        <Link
          to="/dashboard/wallet"
          className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-primary to-primary/85 p-5 text-white shadow-soft transition-all hover:shadow-elegant"
        >
          <div className="pointer-events-none absolute -right-8 -top-8 size-28 rounded-full bg-white/[0.07]" />
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-white/75">Wallet balance</span>
            <Wallet className="size-5 text-white/80" />
          </div>
          <div className="mt-6">
            <p className="font-display text-3xl font-bold">
              {balance === null ? (loading ? "—" : formatNaira(0)) : formatNaira(balance)}
            </p>
            <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-white/85 transition-transform group-hover:translate-x-0.5">
              Open wallet <ChevronRight className="size-3.5" />
            </span>
          </div>
        </Link>

        {/* Quick actions */}
        <div className="grid grid-cols-2 gap-3">
          {quickActions.map((a) => (
            <Link
              key={a.to}
              to={a.to}
              className="group flex flex-col gap-2 rounded-2xl border border-border/60 bg-card p-4 shadow-soft transition-all hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-elegant"
            >
              <span className="grid size-10 place-items-center rounded-xl bg-primary/10 text-primary transition-transform group-hover:scale-105">
                <a.icon className="size-5" />
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground">{a.label}</p>
                <p className="text-[11px] text-muted-foreground">{a.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent bookings */}
      <div className="rounded-2xl border border-border/60 bg-card p-4 shadow-soft sm:p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-base font-bold">Recent bookings</h2>
          <Link to="/dashboard/bookings" className="inline-flex items-center gap-0.5 text-xs font-semibold text-primary hover:underline">
            View all <ChevronRight className="size-3.5" />
          </Link>
        </div>

        {loading ? (
          <div className="flex h-28 items-center justify-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        ) : recent.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border/70 py-10 text-center">
            <CalendarCheck className="mb-2 size-8 text-muted-foreground/30" />
            <p className="text-sm font-semibold">No bookings yet</p>
            <p className="mt-1 text-xs text-muted-foreground">Ready for your next stay?</p>
            <Link to="/hotels" className="mt-4 rounded-full bg-primary px-5 py-2 text-xs font-bold text-primary-foreground transition hover:bg-primary/90">
              Browse Hotels
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {recent.map((b) => {
              const st = STATUS_LABEL[b.status] ?? STATUS_LABEL.pending;
              return (
                <li key={b.id}>
                  <Link
                    to="/dashboard/bookings"
                    className="flex items-center gap-3 py-3 transition-colors hover:bg-accent/40 sm:rounded-xl sm:px-2"
                  >
                    <div className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-xl bg-accent/50">
                      {b.venueImage ? (
                        <img src={resolveMediaUrl(b.venueImage)} alt={b.venueName} className="size-full object-cover" loading="lazy" />
                      ) : b.isEvent ? (
                        <Ticket className="size-5 text-muted-foreground/40" />
                      ) : (
                        <Hotel className="size-5 text-muted-foreground/40" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground">{b.venueName}</p>
                      <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-muted-foreground">
                        {b.venueLocation && (
                          <>
                            <MapPin className="size-3 shrink-0" /> {b.venueLocation} ·{" "}
                          </>
                        )}
                        {fmtDate(b.startDate)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-primary">{formatNaira(b.totalAmount)}</p>
                      <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[9px] font-semibold ${st.cls}`}>
                        {st.label}
                      </span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
